import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import TextAlign from "@tiptap/extension-text-align"
import Underline from "@tiptap/extension-underline"
import Highlight from "@tiptap/extension-highlight"

export default class extends Controller {
  static values = { uploadUrl: { type: String, default: "/admin/uploads" } }

  connect() {
    this.element.style.display = "none"

    this.editorContainer = document.createElement("div")
    this.editorContainer.className = "tiptap-editor border rounded"
    this.element.parentNode.insertBefore(this.editorContainer, this.element.nextSibling)

    this.toolbar = document.createElement("div")
    this.toolbar.className = "tiptap-toolbar border-bottom p-2 bg-light d-flex gap-1 flex-wrap"
    this.toolbar.innerHTML = `
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="bold" title="Bold">
        <strong>B</strong>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="italic" title="Italic">
        <em>I</em>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="underline" title="Underline">
        <u>U</u>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="strike" title="Strikethrough">
        <s>S</s>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="highlight" title="Highlight">
        <span style="background:#fef08a;padding:0 2px;">H</span>
      </button>
      <span class="border-start mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="h1" title="Heading 1">
        H1
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="h2" title="Heading 2">
        H2
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="h3" title="Heading 3">
        H3
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="paragraph" title="Paragraph">
        P
      </button>
      <span class="border-start mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="alignLeft" title="Align Left">
        <i class="ph ph-text-align-left"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="alignCenter" title="Align Center">
        <i class="ph ph-text-align-center"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="alignRight" title="Align Right">
        <i class="ph ph-text-align-right"></i>
      </button>
      <span class="border-start mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="bulletList" title="Bullet List">
        <i class="ph ph-list-bullets"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="orderedList" title="Numbered List">
        <i class="ph ph-list-numbers"></i>
      </button>
      <span class="border-start mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="codeBlock" title="Code Block">
        <i class="ph ph-code"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="blockquote" title="Quote">
        <i class="ph ph-quotes"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="horizontalRule" title="Horizontal Rule">
        <i class="ph ph-minus"></i>
      </button>
      <span class="border-start mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="link" title="Link">
        <i class="ph ph-link"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="image" title="Image">
        <i class="ph ph-image"></i>
      </button>
      <span class="border-start mx-1"></span>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="undo" title="Undo">
        <i class="ph ph-arrow-counter-clockwise"></i>
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tiptap-action="redo" title="Redo">
        <i class="ph ph-arrow-clockwise"></i>
      </button>
    `
    this.editorContainer.appendChild(this.toolbar)
    this.toolbar.addEventListener("click", this.handleToolbarClick.bind(this))

    this.editorContent = document.createElement("div")
    this.editorContent.className = "tiptap-content p-3"
    this.editorContent.style.minHeight = "200px"
    this.editorContainer.appendChild(this.editorContent)

    this.editor = new Editor({
      element: this.editorContent,
      extensions: [
        StarterKit,
        Link.configure({ openOnClick: false }),
        Image.configure({ inline: false }),
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Underline,
        Highlight
      ],
      content: this.element.value,
      onUpdate: ({ editor }) => {
        this.element.value = editor.getHTML()
      },
      onSelectionUpdate: () => this.updateToolbarState(),
      onTransaction: () => this.updateToolbarState()
    })
  }

  disconnect() {
    if (this.editor) this.editor.destroy()
    if (this.editorContainer) this.editorContainer.remove()
  }

  updateToolbarState() {
    const buttons = this.toolbar.querySelectorAll("[data-tiptap-action]")
    buttons.forEach(button => {
      const action = button.dataset.tiptapAction
      let isActive = false

      switch (action) {
        case "bold": isActive = this.editor.isActive("bold"); break
        case "italic": isActive = this.editor.isActive("italic"); break
        case "underline": isActive = this.editor.isActive("underline"); break
        case "strike": isActive = this.editor.isActive("strike"); break
        case "highlight": isActive = this.editor.isActive("highlight"); break
        case "h1": isActive = this.editor.isActive("heading", { level: 1 }); break
        case "h2": isActive = this.editor.isActive("heading", { level: 2 }); break
        case "h3": isActive = this.editor.isActive("heading", { level: 3 }); break
        case "paragraph": isActive = this.editor.isActive("paragraph"); break
        case "alignLeft": isActive = this.editor.isActive({ textAlign: "left" }); break
        case "alignCenter": isActive = this.editor.isActive({ textAlign: "center" }); break
        case "alignRight": isActive = this.editor.isActive({ textAlign: "right" }); break
        case "bulletList": isActive = this.editor.isActive("bulletList"); break
        case "orderedList": isActive = this.editor.isActive("orderedList"); break
        case "codeBlock": isActive = this.editor.isActive("codeBlock"); break
        case "blockquote": isActive = this.editor.isActive("blockquote"); break
        case "link": isActive = this.editor.isActive("link"); break
      }

      button.classList.toggle("active", isActive)
      button.classList.toggle("btn-secondary", isActive)
      button.classList.toggle("btn-outline-secondary", !isActive)
    })
  }

  handleToolbarClick(event) {
    const button = event.target.closest("[data-tiptap-action]")
    if (!button) return

    event.preventDefault()
    const action = button.dataset.tiptapAction

    switch (action) {
      case "bold": this.editor.chain().focus().toggleBold().run(); break
      case "italic": this.editor.chain().focus().toggleItalic().run(); break
      case "underline": this.editor.chain().focus().toggleUnderline().run(); break
      case "strike": this.editor.chain().focus().toggleStrike().run(); break
      case "highlight": this.editor.chain().focus().toggleHighlight().run(); break
      case "h1": this.editor.chain().focus().toggleHeading({ level: 1 }).run(); break
      case "h2": this.editor.chain().focus().toggleHeading({ level: 2 }).run(); break
      case "h3": this.editor.chain().focus().toggleHeading({ level: 3 }).run(); break
      case "paragraph": this.editor.chain().focus().setParagraph().run(); break
      case "alignLeft": this.editor.chain().focus().setTextAlign("left").run(); break
      case "alignCenter": this.editor.chain().focus().setTextAlign("center").run(); break
      case "alignRight": this.editor.chain().focus().setTextAlign("right").run(); break
      case "bulletList": this.editor.chain().focus().toggleBulletList().run(); break
      case "orderedList": this.editor.chain().focus().toggleOrderedList().run(); break
      case "codeBlock": this.editor.chain().focus().toggleCodeBlock().run(); break
      case "blockquote": this.editor.chain().focus().toggleBlockquote().run(); break
      case "horizontalRule": this.editor.chain().focus().setHorizontalRule().run(); break
      case "link": this.insertLink(); break
      case "image": this.insertImage(); break
      case "undo": this.editor.chain().focus().undo().run(); break
      case "redo": this.editor.chain().focus().redo().run(); break
    }
  }

  insertLink() {
    const previousUrl = this.editor.getAttributes("link").href
    const url = window.prompt("Enter URL:", previousUrl || "")

    if (url === null) return
    if (url === "") {
      this.editor.chain().focus().unsetLink().run()
    } else {
      this.editor.chain().focus().setLink({ href: url }).run()
    }
  }

  insertImage() {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = async () => {
      const file = input.files[0]
      if (!file) return

      const formData = new FormData()
      formData.append("file", file)

      try {
        const response = await fetch(this.uploadUrlValue, {
          method: "POST",
          body: formData,
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          }
        })

        if (!response.ok) throw new Error("Upload failed")

        const data = await response.json()
        this.editor.chain().focus().setImage({ src: data.url }).run()
      } catch (error) {
        console.error("Image upload failed:", error)
        alert("Failed to upload image. Please try again.")
      }
    }
    input.click()
  }
}
